import type { NuxtError } from '#app'
import {
  createError,
  useAsyncData,
  useNuxtApp,
  useRuntimeConfig,
} from '#imports'

import type {
  BasePageContent,
  BasePageContentAPI,
  MappedApiPageContent,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { OWNER_BY_PAGE_TYPE } from '../service/content-service.config'

import { useFormatContentResponse } from './useFormatContentResponse'

type ContentEndpoint =
  | '/content/page/:pageType/:pageName'
  | '/content/page/:pageType/:pageName/:pageCategory'

type PathParams = {
  pageType: string
  pageName: string
  pageCategory: string
  locale: string
}

export function useFetchCMSPageContentWithFallback<
  T extends BasePageContentAPI,
  DataT extends MappedApiPageContent<BasePageContent> =
    MappedApiPageContent<BasePageContent>,
>(
  endpoint: ContentEndpoint,
  options: {
    transform?: (data: MappedApiPageContent<T>) => DataT
    timeout?: number
    pathParams: PathParams
  },
) {
  const logger = useLogger()
  const $httpFetch = useHttpFetch()
  const nuxtApp = useNuxtApp()
  const formatContentResponse = useFormatContentResponse()
  const { CMS_PAGE_BACKUP_BASE_URL, CMS_PAGE_TIMEOUT } =
    useRuntimeConfig().public

  const { transform, timeout = CMS_PAGE_TIMEOUT, pathParams } = options

  const owner =
    OWNER_BY_PAGE_TYPE[
      pathParams.pageType as keyof typeof OWNER_BY_PAGE_TYPE
    ] || ''
  const pageCategory = isEmpty(pathParams.pageCategory)
    ? ''
    : `[${pathParams.pageCategory}]`

  const logPrefix = `${owner}[${pathParams.pageType}][${pathParams.pageName}]${pageCategory}`

  return useAsyncData(
    getAsyncDataKey('cms-page-content-with-fallback', {
      pageType: pathParams.pageType,
      pageName: pathParams.pageName,
      pageCategory: pathParams.pageCategory,
      locale: pathParams.locale,
      timeout,
    }),
    async (): Promise<T> => {
      try {
        return (await $httpFetch(endpoint, {
          timeout: Number(timeout),
          pathParams,
        })) as T
      } catch (dataErr) {
        const pageDataError = dataErr as NuxtError

        logger.error(`${logPrefix} ${pageDataError.message}`, {
          owners: ['bot-squad-content-front'],
        })

        // Do not retry the call or fallback to backup if the page does not exist
        if (pageDataError?.status !== undefined && pageDataError.status < 500) {
          throw createError({
            status: pageDataError.status,
            statusText: pageDataError.message,
            fatal: true,
          })
        }

        /**
         * Retry the request if
         * - the timeout was reached
         * - or the service was unavailable (503 error)
         * - or the gateway timed out (504 error)
         * Otherwise, fetch only the backup content
         *  */
        const shouldRetry =
          (dataErr instanceof Error && dataErr.message.includes('timeout')) ||
          pageDataError.status === 503 ||
          pageDataError.status === 504

        const backupEndpoint = isEmpty(pathParams.pageCategory)
          ? '/page-backups/:pageType/:pageName/:locale/backup.json'
          : '/page-backups/:pageType/:pageCategory/:locale/backup.json'

        try {
          // returns if at least one request succeeds
          return (await nuxtApp.runWithContext(
            async () =>
              await Promise.any(
                [
                  $httpFetch(backupEndpoint, {
                    pathParams,
                    baseURL: CMS_PAGE_BACKUP_BASE_URL,
                  }).then((data) => {
                    logger.info(
                      `${logPrefix}[FALLBACK] Using backup endpoint payload`,
                      {
                        owners: ['bot-squad-content-front'],
                      },
                    )

                    return data
                  }),
                ].concat(
                  shouldRetry
                    ? [
                        $httpFetch(endpoint, { pathParams }).then((data) => {
                          logger.info(
                            `${logPrefix}[RETRY] Using content endpoint payload from retry attempt`,
                            {
                              owners: ['bot-squad-content-front'],
                            },
                          )

                          return data
                        }),
                      ]
                    : [],
                ),
              ),
          )) as T
        } catch (fallbackErr) {
          /**
           * All fallback attempts failed
           * - Log the backup request error
           * - Throw the initial content service request error
           */

          const backupError = (fallbackErr as AggregateError).errors[0]
          logger.error(`${logPrefix}[PAGE-BACKUPS] ${backupError}`, {
            owners: ['bot-squad-content-front'],
          })

          throw createError({
            status: pageDataError.status,
            statusText: pageDataError.message,
            fatal: true,
          })
        }
      }
    },
    {
      transform: transform
        ? (data: T) => transform(formatContentResponse(data))
        : (formatContentResponse as (data: T) => DataT),
    },
  )
}
